import { useParams } from "react-router";
import { Empty, Result, Spin } from "antd";
import {
  PlannerLogProvider,
  useWorkspaceData
} from "pages/planning-workspace/hooks/use-workspace-data";
import { applyFilters } from "pages/planning-workspace/adapter";
import TimelineView from "pages/planning-workspace/components/timeline-view";
import { PrintViewLayoutState } from "./model";
import "./style.scss";

// The legend prints as a fixed page footer (see .planner-print-legend-fixed).
// A fixed element repeats on every printed page, pinned to the bottom of the
// page area; this margin is what keeps that 14mm strip off the paper edge.
// Scoped to this component's lifetime — @page is document-wide, and the other
// print views (material log, submittal log, …) must keep their own margins.
const PLANNER_PAGE_STYLE = `
  @page { margin-bottom: 20mm; }
`;

function PlannerPrintLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 bg-white px-6 py-4">
      <span className="inline-flex items-center gap-2 text-xs text-black/85">
        <span className="inline-block h-3 w-3 rounded-full border-2 border-solid border-white bg-ciq-primary-900" />
        Design milestone
      </span>
      <span className="inline-flex items-center gap-2 text-xs text-black/85">
        <span
          className="inline-block h-3 w-8 rounded border border-dashed"
          style={{ borderColor: "#36cfc9", background: "#e8faf7" }}
        />
        Bid Package
      </span>
      <span className="inline-flex items-center gap-2 text-xs text-black/85">
        <span className="inline-block h-px w-6 bg-black/45" />
        Linked milestone
      </span>
    </div>
  );
}

function PlannerPrintView({ state }: { state: PrintViewLayoutState }) {
  const { projectId = "" } = useParams<{ projectId: string }>();
  const {
    model,
    detailsById = {},
    loading,
    error,
    refetch
  } = useWorkspaceData(projectId);
  const filtered = applyFilters(
    model,
    { q: state.query ?? "", status: [] },
    detailsById
  );

  if (loading && model.designPackages.length === 0) {
    return (
      <div className="flex justify-center py-10">
        <Spin size="large" />
      </div>
    );
  }
  if (error) {
    return <Result status="error" title="Couldn't load the planner" />;
  }
  if (filtered.designPackages.length === 0) {
    return <Empty description="No Design Packages to print" />;
  }

  return (
    <div className="planner-print-view" data-testid="planner-print-view">
      <style>{PLANNER_PAGE_STYLE}</style>
      <PlannerLogProvider value={{ detailsById, loading, refetch }}>
        <TimelineView
          projectId={projectId}
          model={filtered}
          collapsedIds={new Set()}
          onToggleLane={() => undefined}
          refetchWorkspace={() => undefined}
          printMode
        />
      </PlannerLogProvider>
      {/* One legend for the whole document (not per design package), pinned
          into the reserved bottom margin of every printed page. */}
      <div
        className="planner-print-legend-fixed"
        data-testid="planner-print-legend"
      >
        <PlannerPrintLegend />
      </div>
    </div>
  );
}

export default PlannerPrintView;
